import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from 'react-toastify';
import axiosInstance from "../../config/axiosInterceptor";
import { checkDatabaseAccess } from "../../utils/accessControl";
import './DatabaseManager.css';

// JSON Editor Modal Component
const JsonEditorModal = ({ isOpen, document, onClose, onSave, mode = 'edit' }) => {
  const [jsonValue, setJsonValue] = useState('');
  const [isValid, setIsValid] = useState(true);

  // Helper function to properly serialize documents for editing
  const serializeDocument = (doc) => {
    return JSON.stringify(doc, (key, value) => {
      // Handle Date objects by keeping them as ISO strings for editing
      if (value instanceof Date) {
        return value.toISOString();
      }
      // Handle MongoDB ObjectId by converting to string
      if (value && typeof value === 'object' && value.$oid) {
        return value.$oid;
      }
      return value;
    }, 2);
  };

  useEffect(() => {
    if (isOpen && document) {
      setJsonValue(serializeDocument(document));
      setIsValid(true);
    }
  }, [isOpen, document]);

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonValue(value);
    
    try {
      JSON.parse(value);
      setIsValid(true);
    } catch (error) {
      setIsValid(false);
    }
  };

  // Helper function to preserve field types when parsing JSON
  const preserveFieldTypes = (originalDoc, newDoc) => {
    if (!originalDoc || mode === 'create') {
      return newDoc; // For new documents, no preservation needed
    }
    
    const preservedDoc = { ...newDoc };
    
    // Preserve specific field types that shouldn't be converted to strings
    const fieldsToPreserve = [
      'createdAt', 'updatedAt', 'timestamp', 'date', 'lastLogin', 'expireAt',
      '_id', 'id', 'userId', 'adminId', 'familyId', 'agencyId', // ObjectIds
      'level', 'coins', 'diamonds', 'amount', 'price', 'count', 'views', 'likes', // Numbers
      'isActive', 'isBlocked', 'isVerified', 'enabled', 'published' // Booleans
    ];
    
    fieldsToPreserve.forEach(field => {
      if (originalDoc.hasOwnProperty(field) && preservedDoc.hasOwnProperty(field)) {
        const originalType = typeof originalDoc[field];
        const originalValue = originalDoc[field];
        const newValue = preservedDoc[field];
        
        // Handle Date objects (they come as strings from JSON)
        if (originalValue instanceof Date || 
            (originalType === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(originalValue))) {
          if (typeof newValue === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(newValue)) {
            preservedDoc[field] = new Date(newValue);
          }
        }
        // Handle ObjectIds (preserve as string but ensure proper format)
        else if (field.toLowerCase().includes('id') && typeof newValue === 'string' && newValue.length === 24) {
          preservedDoc[field] = newValue; // Keep as string for MongoDB ObjectId
        }
        // Handle Numbers
        else if (originalType === 'number' && typeof newValue === 'string' && !isNaN(newValue)) {
          preservedDoc[field] = Number(newValue);
        }
        // Handle Booleans
        else if (originalType === 'boolean' && typeof newValue === 'string') {
          preservedDoc[field] = newValue === 'true' || newValue === true;
        }
      }
    });
    
    return preservedDoc;
  };

  const handleSave = () => {
    if (!isValid) {
      toast.error('Invalid JSON format');
      return;
    }
    
    try {
      const parsedDoc = JSON.parse(jsonValue);
      const preservedDoc = preserveFieldTypes(document, parsedDoc);
      onSave(preservedDoc);
      onClose();
    } catch (error) {
      toast.error('Failed to parse JSON');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content json-editor-modal">
        <div className="modal-header">
          <h3>
            {mode === 'create' ? 'Create New Document' : 'Edit Document'}
            {mode === 'edit' && document?._id && (
              <small style={{ color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                ID: {document._id}
              </small>
            )}
          </h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        <div className="modal-body">
          {mode === 'edit' && (
            <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff', borderRadius: '4px' }}>
              <i className="fa fa-info-circle" style={{ color: '#0066cc', marginRight: '5px' }}></i>
              <small style={{ color: '#0066cc' }}>
                Showing current document content. Make your changes below and click Save.
                <br />
                <strong>Note:</strong> Field types (dates, numbers, booleans) will be automatically preserved during save.
              </small>
            </div>
          )}
          {mode === 'create' && (
            <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
              <i className="fa fa-lightbulb-o" style={{ color: '#856404', marginRight: '5px' }}></i>
              <small style={{ color: '#856404' }}>
                <strong>Tip:</strong> Use proper JSON types: strings in "quotes", numbers without quotes, 
                dates as "YYYY-MM-DDTHH:mm:ss.sssZ", booleans as true/false.
              </small>
            </div>
          )}
          <textarea
            value={jsonValue}
            onChange={handleJsonChange}
            className={`json-editor ${!isValid ? 'invalid' : ''}`}
            placeholder={mode === 'create' ? 'Enter JSON document...' : 'Loading document...'}
            rows={20}
          />
          {!isValid && (
            <div className="error-message">
              <i className="fa fa-exclamation-triangle"></i>
              Invalid JSON format
            </div>
          )}
          <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '12px' }}>
            <strong>Type Examples:</strong>
            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
              <li><code>"createdAt": "2023-10-18T10:30:00.000Z"</code> (Date)</li>
              <li><code>"userId": "507f1f77bcf86cd799439011"</code> (ObjectId)</li>
              <li><code>"level": 5</code> (Number)</li>
              <li><code>"isActive": true</code> (Boolean)</li>
              <li><code>"username": "john_doe"</code> (String)</li>
            </ul>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button 
            onClick={handleSave} 
            className="btn btn-primary"
            disabled={!isValid}
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Date Cleanup Modal Component
const DateCleanupModal = ({ isOpen, collection, onClose, onCleanup }) => {
  const [timeValue, setTimeValue] = useState(1);
  const [timeUnit, setTimeUnit] = useState('months');
  const [dateField, setDateField] = useState('createdAt');
  const [loading, setLoading] = useState(false);

  const handleCleanup = async () => {
    if (!timeValue || timeValue <= 0) {
      toast.error('Please enter a valid time value');
      return;
    }

    const confirmMessage = `Are you sure you want to delete all documents older than ${timeValue} ${timeUnit} in collection "${collection}"?\n\nThis action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      await onCleanup(collection, timeValue, timeUnit, dateField);
      onClose();
    } catch (error) {
      console.error('Cleanup error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content cleanup-modal">
        <div className="modal-header">
          <h3>Clean Up Old Data - {collection}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Date Field:</label>
            <select 
              value={dateField} 
              onChange={(e) => setDateField(e.target.value)}
              className="form-control"
            >
              <option value="createdAt">createdAt</option>
              <option value="updatedAt">updatedAt</option>
              <option value="timestamp">timestamp</option>
              <option value="date">date</option>
            </select>
          </div>
          <div className="form-group">
            <label>Keep data for:</label>
            <div className="time-input-group">
              <input
                type="number"
                min="1"
                value={timeValue}
                onChange={(e) => setTimeValue(parseInt(e.target.value))}
                className="form-control"
                style={{ width: '100px', display: 'inline-block', marginRight: '10px' }}
              />
              <select 
                value={timeUnit} 
                onChange={(e) => setTimeUnit(e.target.value)}
                className="form-control"
                style={{ width: '150px', display: 'inline-block' }}
              >
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
                <option value="years">Years</option>
              </select>
            </div>
          </div>
          <div className="warning-message">
            <i className="fa fa-warning"></i>
            <strong>Warning:</strong> This will permanently delete all documents older than the specified time period. This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button 
            onClick={handleCleanup} 
            className="btn btn-danger"
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin"></i> Deleting...
              </>
            ) : (
              'Delete Old Data'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const DatabaseManager = () => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
  const [jsonModalMode, setJsonModalMode] = useState('edit');
  const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
  const [showQueryHelp, setShowQueryHelp] = useState(false);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [loadingCollectionCount, setLoadingCollectionCount] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-complete pairs for JSON
  const getClosingChar = (openChar) => {
    const pairs = {
      '{': '}',
      '[': ']',
      '"': '"',
      "'": "'"
    };
    return pairs[openChar];
  };

  const handleQueryChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleQueryKeyDown = (e) => {
    const textarea = e.target;
    const { selectionStart, selectionEnd, value } = textarea;
    
    // Handle auto-closing brackets and quotes
    if (e.key === '{' || e.key === '[' || e.key === '"' || e.key === "'") {
      const closingChar = getClosingChar(e.key);
      if (closingChar) {
        e.preventDefault();
        
        const newValue = 
          value.substring(0, selectionStart) + 
          e.key + 
          closingChar + 
          value.substring(selectionEnd);
        
        setSearchQuery(newValue);
        
        // Position cursor between the brackets/quotes
        setTimeout(() => {
          textarea.setSelectionRange(selectionStart + 1, selectionStart + 1);
        }, 0);
      }
    }
    
    // Handle closing bracket navigation
    else if ((e.key === '}' || e.key === ']') && value[selectionStart] === e.key) {
      e.preventDefault();
      textarea.setSelectionRange(selectionStart + 1, selectionStart + 1);
    }
    
    // Handle Enter key for formatting
    else if (e.key === 'Enter') {
      e.preventDefault();
      const beforeCursor = value.substring(0, selectionStart);
      const afterCursor = value.substring(selectionEnd);
      
      // Check if we're inside brackets
      const openBrackets = (beforeCursor.match(/[{[]/g) || []).length;
      const closeBrackets = (beforeCursor.match(/[}\]]/g) || []).length;
      const indent = '  '.repeat(Math.max(0, openBrackets - closeBrackets));
      
      let newValue, newCursorPos;
      
      if (beforeCursor.endsWith('{') && afterCursor.startsWith('}')) {
        // Between empty braces - add proper indentation
        newValue = beforeCursor + '\n' + indent + '  \n' + indent + afterCursor;
        newCursorPos = selectionStart + indent.length + 3;
      } else {
        // Regular enter - just add indentation
        newValue = beforeCursor + '\n' + indent + afterCursor;
        newCursorPos = selectionStart + indent.length + 1;
      }
      
      setSearchQuery(newValue);
      
      setTimeout(() => {
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
    
    // Handle Tab for indentation
    else if (e.key === 'Tab') {
      e.preventDefault();
      const newValue = 
        value.substring(0, selectionStart) + 
        '  ' + 
        value.substring(selectionEnd);
      
      setSearchQuery(newValue);
      
      setTimeout(() => {
        textarea.setSelectionRange(selectionStart + 2, selectionStart + 2);
      }, 0);
    }
  };

  // Check user access level on component mount
  useEffect(() => {
    const accessCheck = checkDatabaseAccess();
    
    if (!accessCheck.hasAccess) {
      setAccessDenied(true);
      console.warn('Database access denied:', accessCheck.error);
      return;
    }
    
    // User has proper access, fetch collections
    fetchCollections();
  }, []);

  // Fetch documents when collection or pagination changes (excluding search)
  useEffect(() => {
    if (selectedCollection && !accessDenied) {
      fetchDocuments();
    }
  }, [selectedCollection, currentPage, entriesPerPage, sortField, sortOrder, accessDenied]);

  // Handle search query clearing
  useEffect(() => {
    if (selectedCollection && !accessDenied && searchQuery === '') {
      fetchDocuments();
    }
  }, [searchQuery]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, []);

  // Show access denied message if user doesn't have proper permissions
  if (accessDenied) {
    return (
      <section className="content-header">
        <div className="row">
          <div className="col-md-12">
            <div className="box">
              <div className="box-body text-center" style={{ padding: '50px' }}>
                <i className="fa fa-lock" style={{ fontSize: '48px', color: '#dc3545', marginBottom: '20px' }}></i>
                <h3 style={{ color: '#dc3545' }}>Access Denied</h3>
                <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
                  You don't have sufficient privileges to access the Database Manager.
                </p>
                <p style={{ fontSize: '14px', color: '#999' }}>
                  This feature requires Super Admin access (Level 1). Please contact your administrator if you believe this is an error.
                </p>
                <Link to="/admin" className="btn btn-primary" style={{ marginTop: '20px' }}>
                  <i className="fa fa-home"></i> Back to Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/database/collections');
      setCollections(response.data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
      toast.error('Failed to fetch collections');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!selectedCollection) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        collection: selectedCollection,
        page: currentPage,
        limit: entriesPerPage,
      });

      if (searchQuery) params.append('search', searchQuery);
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortOrder', sortOrder);
      }

      const response = await axiosInstance.get(`/admin/database/documents?${params.toString()}`);
      setDocuments(response.data.documents || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalDocuments(response.data.totalDocuments || 0);
    } catch (error) {
      console.error('Error fetching documents:', error);
      
      // Show specific error message for query errors
      if (error.response?.status === 400 && error.response?.data?.hint) {
        toast.error(`Query Error: ${error.response.data.message}`, {
          autoClose: 5000
        });
        console.log('Query hint:', error.response.data.hint);
      } else {
        toast.error('Failed to fetch documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCollectionSelect = async (collectionName) => {
    setSelectedCollection(collectionName);
    setCurrentPage(1);
    setDocuments([]);
    setSearchQuery('');
    
    // Close sidebar on mobile after selection
    setSidebarOpen(false);
    
    // Fetch count for this specific collection
    setLoadingCollectionCount(collectionName);
    try {
      const response = await axiosInstance.get(`/admin/database/collection/${collectionName}/count`);
      if (response.data.success) {
        // Update the count in the collections array
        setCollections(prevCollections => 
          prevCollections.map(col => 
            col.name === collectionName 
              ? { ...col, count: response.data.count }
              : col
          )
        );
      }
    } catch (error) {
      console.error('Error fetching collection count:', error);
      // Don't show error toast as it's not critical
    } finally {
      setLoadingCollectionCount(null);
    }
    
    // The useEffect will handle fetching documents when selectedCollection changes
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Validate MongoDB query if it looks like JSON
    if (searchQuery.trim().startsWith('{')) {
      try {
        JSON.parse(searchQuery);
      } catch (error) {
        toast.error('Invalid JSON query format. Please check your syntax.');
        return;
      }
    }
    
    setCurrentPage(1);
    fetchDocuments();
  };

  const handleCreateDocument = () => {
    setSelectedDocument({});
    setJsonModalMode('create');
    setIsJsonModalOpen(true);
  };

  const handleEditDocument = (document) => {
    setSelectedDocument(document);
    setJsonModalMode('edit');
    setIsJsonModalOpen(true);
  };

  const handleSaveDocument = async (document) => {
    try {
      setLoading(true);
      
      if (jsonModalMode === 'create') {
        await axiosInstance.post('/admin/database/document', {
          collection: selectedCollection,
          document: document
        });
        toast.success('Document created successfully');
      } else {
        await axiosInstance.put('/admin/database/document', {
          collection: selectedCollection,
          documentId: selectedDocument._id,
          document: document
        });
        toast.success('Document updated successfully');
      }
      
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.delete('/admin/database/document', {
        data: {
          collection: selectedCollection,
          documentId: documentId
        }
      });
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const handleDateCleanup = async (collection, timeValue, timeUnit, dateField) => {
    try {
      const response = await axiosInstance.post('/admin/database/cleanup', {
        collection: collection,
        timeValue: timeValue,
        timeUnit: timeUnit,
        dateField: dateField
      });

      const deletedCount = response.data.deletedCount || 0;
      toast.success(`Successfully deleted ${deletedCount} old documents from ${collection}`);
      
      if (collection === selectedCollection) {
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast.error('Failed to cleanup old data');
    }
  };

  const formatDocumentPreview = (doc) => {
    const preview = { ...doc };
    // Remove _id for cleaner preview and limit nested objects
    delete preview._id;
    
    const jsonStr = JSON.stringify(preview, null, 2);
    return jsonStr.length > 200 ? jsonStr.substring(0, 200) + '...' : jsonStr;
  };

  const filteredCollections = collections.filter(collection =>
    collection.name.toLowerCase().includes(collectionSearch.toLowerCase())
  );

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
          <button onClick={() => paginate(i)} className="page-link">
            {i}
          </button>
        </li>
      );
    }

    return pageNumbers;
  };

  return (
    <>
      <section className="content-header">
        <h1>Database Manager</h1>
        <ol className="breadcrumb">
          <li>
            <Link to="/admin">
              <i className="fa fa-dashboard"></i> Home
            </Link>
          </li>
          <li className="active">Database Manager</li>
        </ol>
      </section>

      <section className="content">
        {/* Mobile Sidebar Toggle Button */}
        <button 
          className="mobile-sidebar-toggle"
          onClick={toggleSidebar}
          title="Toggle Collections"
        >
          <i className="fa fa-database"></i>
        </button>

        {/* Mobile Sidebar Overlay */}
        <div 
          className={`collections-sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={closeSidebar}
        ></div>

        <div className="row">
          {/* Collections Sidebar - Sticky */}
          <div className={`col-md-3 collections-sidebar-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
            <div className="box collections-sidebar-box">
              <div className="box-header collections-sidebar-header">
                <h3 className="box-title">
                  <i className="fa fa-database"></i> Collections
                  <span className="badge bg-blue" style={{ marginLeft: '8px' }}>
                    {filteredCollections.length}
                  </span>
                </h3>
                {/* Mobile Close Button */}
                <button 
                  className="sidebar-close-btn d-md-none"
                  onClick={closeSidebar}
                  title="Close"
                >
                  ×
                </button>
              </div>
              <div className="box-body collections-sidebar-body">
                {/* Collection Search - Fixed at top */}
                <div className="collection-search">
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search collections..."
                      value={collectionSearch}
                      onChange={(e) => setCollectionSearch(e.target.value)}
                      className="form-control"
                      style={{
                        fontSize: '12px',
                        paddingRight: '30px'
                      }}
                    />
                    <i 
                      className="fa fa-search" 
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#999',
                        fontSize: '12px'
                      }}
                    ></i>
                    {collectionSearch && (
                      <button
                        type="button"
                        onClick={() => setCollectionSearch('')}
                        style={{
                          position: 'absolute',
                          right: '25px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          fontSize: '14px',
                          cursor: 'pointer',
                          color: '#999'
                        }}
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {/* Collections List - Scrollable */}
                <div className="collections-list-container">
                  {loading && !collections.length ? (
                    <div className="loading-state">
                      <i className="fa fa-spinner fa-spin"></i> Loading...
                    </div>
                  ) : filteredCollections.length === 0 ? (
                    <div className="empty-state" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                      <i className="fa fa-search" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                      <p style={{ margin: '0', fontSize: '12px' }}>
                        {collectionSearch ? 'No collections match your search' : 'No collections found'}
                      </p>
                    </div>
                  ) : (
                    <ul className="collections-list">
                      {filteredCollections.map((collection) => (
                        <li 
                          key={collection.name}
                          className={`collection-item ${selectedCollection === collection.name ? 'active' : ''}`}
                          onClick={() => handleCollectionSelect(collection.name)}
                        >
                          <div className="collection-info">
                            <span className="collection-name">{collection.name}</span>
                            <span className="collection-count">
                              {loadingCollectionCount === collection.name ? (
                                <><i className="fa fa-spinner fa-spin"></i> counting...</>
                              ) : collection.count !== null ? (
                                `${collection.count} docs`
                              ) : (
                                '...'
                              )}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Documents View */}
          <div className="col-md-9 mobile-documents-container">
            <div className="box">
              <div className="box-header">
                <h3 className="box-title">
                  {selectedCollection ? (
                    <>
                      <i className="fa fa-table"></i> {selectedCollection}
                      <span className="collection-stats">({totalDocuments} documents)</span>
                    </>
                  ) : (
                    'Select a collection to view documents'
                  )}
                </h3>
                {selectedCollection && (
                  <div className="box-tools">
                    <button 
                      onClick={handleCreateDocument}
                      className="btn btn-success btn-sm"
                    >
                      <i className="fa fa-plus"></i> Create Document
                    </button>
                    <button 
                      onClick={() => setIsCleanupModalOpen(true)}
                      className="btn btn-warning btn-sm"
                    >
                      <i className="fa fa-calendar-times-o"></i> Clean Old Data
                    </button>
                  </div>
                )}
              </div>
              
              {selectedCollection && (
                <>
                  <div className="box-body">
                    {/* Search and Controls */}
                    <div className="row controls-row">
                      <div className="col-md-3">
                        <label>Entries per page:</label>
                        <select
                          value={entriesPerPage}
                          onChange={(e) => setEntriesPerPage(parseInt(e.target.value))}
                          className="form-control"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label>Sort by:</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <input
                            type="text"
                            placeholder="Field name (e.g., createdAt)"
                            value={sortField}
                            onChange={(e) => setSortField(e.target.value)}
                            className="form-control"
                          />
                          <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="form-control"
                            style={{ width: '100px' }}
                          >
                            <option value="desc">Desc</option>
                            <option value="asc">Asc</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label>MongoDB Query:</label>
                        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '5px' }}>
                          <div style={{ position: 'relative', flex: 1 }}>
                            <textarea
                              placeholder='e.g. {"userId": "123"} or {"status": "active"}'
                              value={searchQuery}
                              onChange={handleQueryChange}
                              onKeyDown={handleQueryKeyDown}
                              className="form-control query-textarea"
                              title="Enter MongoDB query object. Auto-complete: {}, [], quotes. Tab for indent, Enter for formatting."
                              rows={3}
                            />
                            {searchQuery && (
                              <button
                                type="button"
                                onClick={() => {
                                  setSearchQuery('');
                                  setCurrentPage(1);
                                  // The useEffect will handle re-fetching when searchQuery changes
                                }}
                                style={{
                                  position: 'absolute',
                                  right: '8px',
                                  top: '8px',
                                  background: 'none',
                                  border: 'none',
                                  fontSize: '16px',
                                  cursor: 'pointer',
                                  color: '#999'
                                }}
                                title="Clear search"
                              >
                                ×
                              </button>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <button type="submit" className="btn btn-primary" title="Execute search query">
                              <i className="fa fa-search"></i>
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-info"
                              onClick={() => setShowQueryHelp(!showQueryHelp)}
                              title="Show query examples"
                            >
                              <i className="fa fa-question"></i>
                            </button>
                          </div>
                        </form>
                        <div style={{ marginTop: '5px' }}>
                          <small style={{ color: '#666', fontSize: '11px', display: 'block' }}>
                            <strong>Auto-complete:</strong> 
                            <span style={{ color: '#28a745', marginLeft: '5px' }}>{ } [ ] " "</span>
                            <span style={{ marginLeft: '10px' }}>
                              <strong>Keys:</strong> Tab→indent, Enter→format
                            </span>
                          </small>
                          <small style={{ color: '#666', fontSize: '11px', display: 'block', marginTop: '2px' }}>
                            <strong>Examples:</strong> 
                            <span 
                              onClick={() => setSearchQuery('{"status": "active"}')}
                              style={{ cursor: 'pointer', color: '#007cba', marginLeft: '5px', textDecoration: 'underline' }}
                            >
                              {"{"}"status": "active"{"}"}
                            </span>
                            <span 
                              onClick={() => setSearchQuery('{"userId": "' + (documents[0]?._id || '123') + '"}')}
                              style={{ cursor: 'pointer', color: '#007cba', marginLeft: '10px', textDecoration: 'underline' }}
                            >
                              {"{"}"userId": "id"{"}"}
                            </span>
                            <span 
                              onClick={() => setSearchQuery('{"createdAt": {"$gte": "2024-01-01"}}')}
                              style={{ cursor: 'pointer', color: '#007cba', marginLeft: '10px', textDecoration: 'underline' }}
                            >
                              Date range
                            </span>
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* MongoDB Query Help Section */}
                    {showQueryHelp && (
                      <div className="row" style={{ marginBottom: '20px' }}>
                        <div className="col-md-12">
                          <div className="box box-info">
                            <div className="box-header">
                              <h4 className="box-title">
                                <i className="fa fa-info-circle"></i> MongoDB Query Examples
                              </h4>
                              <button 
                                onClick={() => setShowQueryHelp(false)}
                                className="close"
                                style={{ float: 'right', background: 'none', border: 'none', fontSize: '18px' }}
                              >
                                ×
                              </button>
                            </div>
                            <div className="box-body">
                              <div className="row">
                                <div className="col-md-6">
                                  <h5><i className="fa fa-search"></i> Basic Queries</h5>
                                  <div className="query-examples">
                                    <div className="query-example">
                                      <strong>Find by field:</strong>
                                      <code onClick={() => setSearchQuery('{"status": "active"}')} style={{ cursor: 'pointer', display: 'block', padding: '5px', backgroundColor: '#f5f5f5', marginTop: '2px' }}>
                                        {"{"}"status": "active"{"}"}
                                      </code>
                                    </div>
                                    <div className="query-example">
                                      <strong>Find by ObjectId:</strong>
                                      <code onClick={() => setSearchQuery('{"userId": "' + (documents[0]?._id || '507f1f77bcf86cd799439011') + '"}')} style={{ cursor: 'pointer', display: 'block', padding: '5px', backgroundColor: '#f5f5f5', marginTop: '2px' }}>
                                        {"{"}"userId": "ObjectId"{"}"}
                                      </code>
                                    </div>
                                    <div className="query-example">
                                      <strong>Multiple conditions:</strong>
                                      <code onClick={() => setSearchQuery('{"status": "active", "type": "user"}')} style={{ cursor: 'pointer', display: 'block', padding: '5px', backgroundColor: '#f5f5f5', marginTop: '2px' }}>
                                        {"{"}"status": "active", "type": "user"{"}"}
                                      </code>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <h5><i className="fa fa-calendar"></i> Date & Advanced Queries</h5>
                                  <div className="query-examples">
                                    <div className="query-example">
                                      <strong>Date range:</strong>
                                      <code onClick={() => setSearchQuery('{"createdAt": {"$gte": "2024-01-01"}}')} style={{ cursor: 'pointer', display: 'block', padding: '5px', backgroundColor: '#f5f5f5', marginTop: '2px' }}>
                                        {"{"}"createdAt": {"{"}"$gte": "2024-01-01"{"}"}{"}"}
                                      </code>
                                    </div>
                                    <div className="query-example">
                                      <strong>Text contains:</strong>
                                      <code onClick={() => setSearchQuery('{"name": {"$regex": "john", "$options": "i"}}')} style={{ cursor: 'pointer', display: 'block', padding: '5px', backgroundColor: '#f5f5f5', marginTop: '2px' }}>
                                        {"{"}"name": {"{"}"$regex": "john", "$options": "i"{"}"}{"}"}
                                      </code>
                                    </div>
                                    <div className="query-example">
                                      <strong>Number range:</strong>
                                      <code onClick={() => setSearchQuery('{"age": {"$gte": 18, "$lt": 65}}')} style={{ cursor: 'pointer', display: 'block', padding: '5px', backgroundColor: '#f5f5f5', marginTop: '2px' }}>
                                        {"{"}"age": {"{"}"$gte": 18, "$lt": 65{"}"}{"}"}
                                      </code>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="row" style={{ marginTop: '15px' }}>
                                <div className="col-md-12">
                                  <h5><i className="fa fa-code"></i> MongoDB Operators</h5>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    <span className="label label-info">$eq (equals)</span>
                                    <span className="label label-info">$ne (not equal)</span>
                                    <span className="label label-info">$gt (greater than)</span>
                                    <span className="label label-info">$gte (greater than or equal)</span>
                                    <span className="label label-info">$lt (less than)</span>
                                    <span className="label label-info">$lte (less than or equal)</span>
                                    <span className="label label-info">$in (in array)</span>
                                    <span className="label label-info">$regex (pattern match)</span>
                                    <span className="label label-info">$exists (field exists)</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Documents Table */}
                    <div className="documents-container">
                      {loading ? (
                        <div className="loading-state">
                          <i className="fa fa-spinner fa-spin"></i> Loading documents...
                        </div>
                      ) : documents.length === 0 ? (
                        <div className="empty-state">
                          <i className="fa fa-file-o"></i>
                          <p>No documents found in this collection</p>
                        </div>
                      ) : (
                        <table className="table table-bordered table-striped documents-table">
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}>Sr.</th>
                              <th style={{ width: '120px' }}>Document ID</th>
                              <th>Document Preview</th>
                              <th style={{ width: '150px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {documents.map((doc, index) => {
                              const srNo = index + 1 + (currentPage - 1) * entriesPerPage;
                              return (
                                <tr key={doc._id}>
                                  <td>{srNo}</td>
                                  <td className="document-id">{doc._id}</td>
                                  <td>
                                    <pre className="document-preview">
                                      {formatDocumentPreview(doc)}
                                    </pre>
                                  </td>
                                  <td>
                                    <div className="action-buttons">
                                      <button
                                        onClick={() => handleEditDocument(doc)}
                                        className="btn btn-primary btn-xs"
                                        title="Edit Document"
                                      >
                                        <i className="fa fa-edit"></i>
                                      </button>
                                      <button
                                        onClick={() => handleDeleteDocument(doc._id)}
                                        className="btn btn-danger btn-xs"
                                        title="Delete Document"
                                      >
                                        <i className="fa fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="row">
                        <div className="col-md-12">
                          <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                              <button
                                onClick={() => paginate(currentPage - 1)}
                                className="page-link"
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            {renderPageNumbers()}
                            <li className="page-item disabled">
                              <span className="page-link">Page {currentPage} of {totalPages}</span>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                              <button
                                onClick={() => paginate(currentPage + 1)}
                                className="page-link"
                                disabled={currentPage === totalPages}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* JSON Editor Modal */}
      <JsonEditorModal
        isOpen={isJsonModalOpen}
        document={selectedDocument}
        onClose={() => setIsJsonModalOpen(false)}
        onSave={handleSaveDocument}
        mode={jsonModalMode}
      />

      {/* Date Cleanup Modal */}
      <DateCleanupModal
        isOpen={isCleanupModalOpen}
        collection={selectedCollection}
        onClose={() => setIsCleanupModalOpen(false)}
        onCleanup={handleDateCleanup}
      />
    </>
  );
};

export default DatabaseManager;
